import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {cloneDeep, isEqual} from 'lodash';

import Button from 'common/components/button';
import ColorPicker from 'common/components/color-picker';
import Input from 'common/components/input';
import SvgSymbol from 'common/components/svg-symbol';
import Colors from 'common/constants/colors';
import {t} from 'common/utils/translation-util';
import {arrayOfTypes} from 'common/validators';

import './index.scss';

export const emptyRange = () => cloneDeep([Colors.green500, ['', '']]);
export const isEmpty = (value) => !value && value !== 0;
export const isVal = (value) => !isEmpty(value);

export const parseInitialRanges = (value) => {
  let initialRanges;
  if (value) {
    if (Array.isArray(value)) {
      initialRanges = value;
    } else if (typeof value === 'string') {
      initialRanges = JSON.parse(value);
    }
  }
  return initialRanges;
};

export const addRange = (ranges) => {
  const newRanges = cloneDeep(ranges);
  newRanges.push(emptyRange());
  return newRanges;
};

export const changeRangeColor = (ranges, rangeIndex, color) => {
  const newRanges = cloneDeep(ranges);
  newRanges[rangeIndex][0] = color;
  return newRanges;
};

export const changeRangeValue = (ranges, rangeIndex, valIndex, value) => {
  let parsedValue = value;
  if (isNaN(value) || value === undefined || value === null) {
    parsedValue = '';
  }
  if (parsedValue !== '') {
    parsedValue = parseFloat(parsedValue);
  }
  const newRanges = cloneDeep(ranges);
  newRanges[rangeIndex][1][valIndex] = parsedValue;
  return newRanges;
};

export const deleteRange = (ranges, rangeIndex) => {
  const newRanges = cloneDeep(ranges);
  newRanges.splice(rangeIndex, 1);
  return newRanges;
};

export const getColorForValue = (value, ranges, nullColor) => {
  // low value inclusive, high value exclusive
  const matches = ranges.filter((range, rangeIndex) => {
    const isFirst = rangeIndex === 0;
    const isLast = rangeIndex === ranges.length - 1;
    const isOnly = ranges.length === 1;
    const [low, high] = range[1];

    if (isVal(low) && isVal(high)) {
      if (value >= low && value < high) {
        return true;
      }
    } else if (isFirst && isVal(high) && value < high) {
      return true;
    } else if (isLast && isVal(low) && value >= low) {
      return true;
    }
    if (isOnly && !isVal(low) && !isVal(high)) {
      return true;
    }
    return false;
  });
  if (matches.length) {
    return matches[0][0];
  }
  return nullColor;
};

export const ERRORS = {
  HIGH_REQUIRED_ERROR: t('High value required.'),
  LOW_REQUIRED_ERROR: t('Low value required.'),
  ORDERING_ERROR: t('Values must be in consecutive order.'),
  OVERLAP_ERROR: t('Range overlaps with another range.'),
  REQUIRED_ERROR: t('Value required.'),
};

export const validateRanges = (ranges) => {
  // Note: mirrors server-side validation logic
  let prevHigh = null;
  return ranges.map((range, rangeIndex) => {
    const errors = [];
    const isFirst = rangeIndex === 0;
    const isLast = rangeIndex === ranges.length - 1;
    const isOnly = ranges.length === 1;
    const [low, high] = range[1];

    if (isVal(low) && isVal(high) && low > high) {
      errors.push(ERRORS.ORDERING_ERROR);
    }

    if (isVal(low) && isVal(prevHigh) && low < prevHigh) {
      errors.push(ERRORS.OVERLAP_ERROR);
    }

    if (isFirst && isEmpty(high) && !isOnly) {
      errors.push(ERRORS.HIGH_REQUIRED_ERROR);
    }
    if (isLast && isEmpty(low) && !isOnly) {
      errors.push(ERRORS.LOW_REQUIRED_ERROR);
    }
    if (!isFirst && !isLast && (isEmpty(high) || isEmpty(low))) {
      errors.push(ERRORS.REQUIRED_ERROR);
    }

    prevHigh = high;

    return errors;
  });
};

/**
 * RangeColorPicker
 */
const RangeColorPicker = ({colors, initialRanges, onUpdate}) => {
  const [ranges, setRanges] = useState(initialRanges);
  const [errors, setErrors] = useState([]);

  const rangeErrors = validateRanges(ranges);
  if (!isEqual(errors, rangeErrors)) {
    setErrors(rangeErrors);
  }

  useEffect(() => onUpdate(ranges));

  return (
    <div className={'RangeColorPicker'}>
      {ranges.map(([color, [low, high]], rangeIndex) => {
        return (
          <div
            className={'RangeColorPicker-range'}
            key={`range-${color}-${rangeIndex}`}>
            <Input
              onChange={(evt) =>
                setRanges(
                  changeRangeValue(ranges, rangeIndex, 0, evt.target.value),
                )
              }
              type={'number'}
              value={low === null ? '' : low}
            />
            <span className={'RangeColorPicker-to-text'}>{'to'}</span>
            <Input
              onChange={(evt) =>
                setRanges(
                  changeRangeValue(ranges, rangeIndex, 1, evt.target.value),
                )
              }
              type={'number'}
              value={high === null ? '' : high}
            />
            <ColorPicker
              color={color}
              colors={colors}
              handleChange={(newColor) =>
                setRanges(changeRangeColor(ranges, rangeIndex, newColor))
              }
              position={'top-left'}
            />
            <span onClick={() => setRanges(deleteRange(ranges, rangeIndex))}>
              <SvgSymbol name={'delete'} />
            </span>
            {errors[rangeIndex]
              ? errors[rangeIndex].map((error, errorIndex) => (
                  <div
                    className={'RangeColorPicker-error'}
                    key={`error-${rangeIndex}-${errorIndex}`}>
                    {error}
                  </div>
                ))
              : null}
          </div>
        );
      })}
      <span
        className={'RangeColorPicker-add-button'}
        onClick={() => setRanges(addRange(ranges))}>
        <Button svgName={'plus'} type={'blue'} withIcon={true}>
          <span className={'Button-children'}>{t('Add a range')}</span>
        </Button>
      </span>
    </div>
  );
};

RangeColorPicker.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.array),
  initialRanges: PropTypes.arrayOf(
    arrayOfTypes([
      PropTypes.string,
      PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ),
    ]),
  ),
  onUpdate: PropTypes.func,
};

RangeColorPicker.defaultProps = {
  colors: undefined,
  initialRanges: [emptyRange()],
  onUpdate: () => {},
};

export default RangeColorPicker;
